import { createFileRoute, Link, Outlet, useNavigate } from '@tanstack/react-router';
import {
  FolderTree,
  Image as ImageIcon,
  Languages,
  LayoutDashboard,
  LogOut,
  Package,
  ReceiptText,
  ScrollText,
  ShieldAlert,
  Star,
  Users,
} from 'lucide-react';
import type { AdminRole, Permission } from '@nokowazzi/types';
import { usePermissions } from '../features/admin/use-permissions';
import { requireAdminBeforeLoad } from '../features/auth/guard';
import { queryClient } from '../lib/query-client';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/auth';

export const Route = createFileRoute('/_admin')({
  beforeLoad: async ({ location }) => {
    await requireAdminBeforeLoad(location.href);
  },
  component: AdminLayout,
});

interface MenuItem {
  to:
    | '/'
    | '/orders'
    | '/disputes'
    | '/users'
    | '/reviews'
    | '/audit'
    | '/products'
    | '/banners'
    | '/categories'
    | '/translation';
  label: string;
  icon: typeof LayoutDashboard;
  perm: Permission;
  exact?: boolean;
}

const MENU: MenuItem[] = [
  { to: '/', label: '대시보드', icon: LayoutDashboard, perm: 'dashboard:read', exact: true },
  { to: '/orders', label: '거래', icon: ReceiptText, perm: 'orders:read' },
  { to: '/disputes', label: '분쟁', icon: ShieldAlert, perm: 'disputes:read' },
  { to: '/users', label: '회원', icon: Users, perm: 'users:read' },
  { to: '/reviews', label: '리뷰', icon: Star, perm: 'reviews:read' },
  { to: '/products', label: '상품 관리', icon: Package, perm: 'products:read' },
  { to: '/banners', label: '배너', icon: ImageIcon, perm: 'curation:read' },
  { to: '/categories', label: '카테고리', icon: FolderTree, perm: 'categories:read' },
  { to: '/translation', label: '번역 설정', icon: Languages, perm: 'translation:read' },
  { to: '/audit', label: '감사 로그', icon: ScrollText, perm: 'audit:read' },
];

const ROLE_LABEL: Record<AdminRole, string> = {
  super_admin: '최고 관리자',
  operator: '운영자',
  cs: 'CS',
  finance: '재무',
  viewer: '뷰어',
};

function AdminLayout() {
  const navigate = useNavigate();
  const session = useAuthStore((state) => state.session);
  const { role, can } = usePermissions();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    queryClient.clear();
    await navigate({ to: '/signin' });
  };

  return (
    <div className="min-h-dvh bg-page">
      {/* 좌측 고정 사이드바 (240px) */}
      <aside className="fixed inset-y-0 left-0 z-40 flex w-60 flex-col bg-inverse text-fg-inverse">
        <div className="flex items-baseline gap-1.5 px-5 pb-4 pt-6">
          <span className="text-title3 font-bold text-gold-500">NOKOWAZZI</span>
          <span className="text-caption2 tracking-wide text-neutral-400">ADMIN</span>
        </div>
        <nav aria-label="어드민 메뉴" className="flex flex-1 flex-col gap-1 px-3 pt-2">
          {MENU.filter((item) => can(item.perm)).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              activeOptions={{ exact: item.exact ?? false }}
              className="flex items-center gap-3 rounded-md px-3 py-2.5 text-caption1 font-medium transition-colors"
              activeProps={{ className: 'bg-neutral-800 text-gold-500' }}
              inactiveProps={{
                className: 'text-neutral-400 hover:bg-neutral-800 hover:text-fg-inverse',
              }}
            >
              <item.icon className="size-4 shrink-0" aria-hidden="true" />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="border-t border-neutral-800 px-5 py-4">
          <p className="truncate text-caption1 text-fg-inverse">{session?.user.email ?? ''}</p>
          <p className="mt-0.5 text-caption2 text-neutral-400">{role != null ? ROLE_LABEL[role] : ''}</p>
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="mt-2 flex min-h-9 items-center gap-2 rounded-md text-caption1 text-neutral-400 transition-colors hover:text-fg-inverse focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-600"
          >
            <LogOut className="size-4" aria-hidden="true" />
            로그아웃
          </button>
        </div>
      </aside>

      {/* 우측 컨텐츠 */}
      <main className="ml-60 min-h-dvh">
        <div className="mx-auto w-full max-w-6xl px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
